import { z } from "zod";
import { businessUnitTypeSchema } from "../auth/features.js";
import { taxRateCodeSchema } from "../masterdata/tax.js";
import { localDateSchema, moneySchema, nonNegativeMoneySchema, quantitySchema, timestampSchema, unitPriceSchema, uuidSchema, } from "../common/primitives.js";
import { shiftClosingStatusSchema, shiftClosingWarningSchema, shiftMemberDutySchema, shiftSettlementTargetSchema, shiftTenderTypeSchema, } from "./enums.js";
import { denominationSchema } from "./records.js";
import { posExpectedSourceSchema } from "../pos/enums.js";
import { shiftSchema, shiftTankReadingInputSchema, shiftTotalizerInputSchema } from "./shifts.js";
/**
 * Vardiya kapanışı ve pompacı / kasiyer hesabı (Prompt 13 — API B aşaması).
 *
 * Değişmezler:
 *  · **Beklenen tutar ENDEKSTEN gelir** (ADR 0013 karar 5): Σ (tabanca × aralık) endeks farkı × fiyat dilimi.
 *    Aralıktaki İŞLEM kayıtları yalnızca dilim payını ve veri bütünlüğü uyarısını belirler.
 *  · Beyan = sayılan nakit − devir parası + ara teslimler + nakitten ödenen masraflar + nakit dışı tahsilatlar.
 *    `ON_ACCOUNT` / `FLEET` satırları İSTEMCİDEN ALINMAZ: vardiya içi veresiye / filo fişlerinden türetilir.
 *  · Fark = beyan − beklenen. Tolerans içi kendiliğinden kabul; dışı onay motoruna (`SHIFT_DIFFERENCE`) gider.
 *  · Kabul edilen hesap DONDURULUR; değişiklik için hesap yeniden açılır (kayıtlar ters çevrilir).
 */
const reasonSchema = z.string().trim().min(3).max(500);
const explanationSchema = z.string().trim().min(1).max(1000);
// ── Kapanışı başlat / geri al ────────────────────────────────────────────────
export const startShiftClosingSchema = z.object({
    at: timestampSchema
        .optional()
        .describe("Kapanış endekslerinin alındığı an = satış penceresinin sonu. Yoksa şimdi"),
    totalizers: z
        .array(shiftTotalizerInputSchema)
        .max(200)
        .optional()
        .describe("Elle okunan kapanış endeksleri; açılıştaki kurallar aynen geçerlidir"),
    tankReadings: z.array(shiftTankReadingInputSchema).max(50).optional(),
});
export const reopenShiftSchema = z.object({
    reason: reasonSchema.describe("Kapanışın neden geri alındığı"),
});
// ── Beyan (hesap kaydetme) ───────────────────────────────────────────────────
/**
 * İstemciden gelen tahsilat satırı. `ON_ACCOUNT` ve `FLEET` REDDEDİLİR (`SHIFT_TENDER_DERIVED`):
 * onlar vardiya içi fişlerden cari bazında türetilir.
 */
export const shiftTenderInputSchema = z.object({
    tenderType: shiftTenderTypeSchema,
    posTerminalId: uuidSchema
        .nullable()
        .optional()
        .describe("`CARD` için ZORUNLU (istasyonun etkin terminali)"),
    amount: moneySchema.refine((value) => BigInt(value) > 0n, "Tutar sıfırdan büyük olmalıdır"),
    slipCount: z.number().int().min(0).max(100000).nullable().optional(),
    reference: z
        .string()
        .trim()
        .min(1)
        .max(60)
        .nullable()
        .optional()
        .describe("POS gün sonu no, kupon serisi…"),
});
/**
 * KASİYER hesabının satış özeti. Prompt 15'e kadar YALNIZCA elle girilirdi; artık istasyonda yerel kasa
 * varsa AYNI biçim `pos_sales`'ten TÜRETİLİR (ADR 0015) — gün sonu özet fişi ve Z karşılaştırması
 * değişmeden çalışsın diye. Bu yüzden `gross` İŞARETLİDİR: bir gelir grubunda iade satıştan fazlaysa
 * satır negatif olabilir (elle girişte pratikte olmaz, POS türetmesinde olabilir).
 */
export const shiftManualSaleSchema = z.object({
    businessUnit: businessUnitTypeSchema,
    revenueGroup: z
        .string()
        .trim()
        .regex(/^[A-Z][A-Z0-9_]{1,39}$/, "Gelir grubu ürün muhasebe grubu biçiminde olmalıdır (ör. FUEL_DIESEL)")
        .describe("`SALES_REVENUE:{grup}` parametresi (ürünün muhasebe grubu)"),
    vatRateCode: taxRateCodeSchema,
    gross: moneySchema.describe("KDV DÂHİL satış tutarı (kuruş); POS türetmesinde iade fazlaysa negatif olabilir"),
});
export const saveShiftClosingSchema = z.object({
    version: z.number().int().min(1).describe("İyimser kilit"),
    cashDenominations: z
        .array(denominationSchema)
        .max(30)
        .optional()
        .describe("Küpür sayımı; verilirse sayılan nakit BURADAN hesaplanır"),
    cashCounted: nonNegativeMoneySchema
        .optional()
        .describe("Küpür verilmediyse toplam sayım; ikisi de verilirse tutarlı olmalıdır"),
    tenders: z.array(shiftTenderInputSchema).max(100).optional(),
    manualSales: z.array(shiftManualSaleSchema).max(100).optional().describe("Yalnızca KASİYER hesabında"),
    explanation: explanationSchema.nullable().optional().describe("Pompacının fark açıklaması"),
});
export const submitShiftClosingSchema = z.object({
    version: z.number().int().min(1),
    explanation: explanationSchema.nullable().optional(),
});
export const reopenShiftClosingSchema = z.object({ reason: reasonSchema });
export const closeShiftSchema = z.object({
    note: z.string().trim().min(1).max(1000).nullable().optional(),
});
export const approveShiftSchema = z.object({
    note: z.string().trim().min(1).max(1000).nullable().optional(),
});
export const revokeShiftApprovalSchema = z.object({ reason: reasonSchema });
// ── Beklenen tutar dökümü ────────────────────────────────────────────────────
export const shiftExpectedSegmentSchema = z.object({
    from: timestampSchema,
    to: timestampSchema,
    unitPrice: unitPriceSchema.describe("KDV dâhil ₺/L"),
    totalizerLitres: quantitySchema,
    testLitres: quantitySchema,
    saleLitres: quantitySchema,
    amount: moneySchema,
});
export const shiftExpectedIntervalSchema = z.object({
    assignmentId: uuidSchema,
    nozzleId: uuidSchema,
    nozzleLabel: z.string(),
    productId: uuidSchema,
    productName: z.string(),
    from: timestampSchema,
    to: timestampSchema,
    openingTotalizer: quantitySchema,
    closingTotalizer: quantitySchema.nullable(),
    totalizerLitres: quantitySchema,
    testLitres: quantitySchema,
    saleLitres: quantitySchema,
    amount: moneySchema,
    transactionLitres: quantitySchema,
    transactionAmount: moneySchema,
    segments: z.array(shiftExpectedSegmentSchema),
    warnings: z.array(shiftClosingWarningSchema),
});
export const shiftExpectedDetailSchema = z.object({
    amount: moneySchema,
    saleLitres: quantitySchema,
    testLitres: quantitySchema,
    byProduct: z.array(z.object({
        productId: uuidSchema,
        productName: z.string(),
        saleLitres: quantitySchema,
        testLitres: quantitySchema,
        amount: moneySchema,
    })),
    intervals: z.array(shiftExpectedIntervalSchema),
    manualSales: z.array(shiftManualSaleSchema),
    /**
     * KASİYER hesabında beklenenin KAYNAĞI (ADR 0015). `POS` = istasyonun yerel kasa satışları,
     * `MANUAL` = elle girilen özet. Pompacı hesabında her zaman `MANUAL`'dir (beklenen endekstendir).
     * `.catch` Prompt 15 ÖNCESİ dondurulmuş hesaplar içindir: alanı olmayan eski jsonb `MANUAL` okunur.
     */
    expectedSource: posExpectedSourceSchema.catch("MANUAL"),
});
// ── Hesap ────────────────────────────────────────────────────────────────────
export const shiftClosingTenderSchema = z.object({
    id: uuidSchema,
    tenderType: shiftTenderTypeSchema,
    posTerminalId: uuidSchema.nullable(),
    posTerminalName: z.string().nullable(),
    counterpartyId: uuidSchema.nullable(),
    counterpartyName: z.string().nullable(),
    amount: moneySchema,
    slipCount: z.number().int().nullable(),
    reference: z.string().nullable(),
    /** true → satır fişlerden TÜRETİLDİ; istemci gönderemez ve değiştiremez. */
    derived: z.boolean(),
});
const closingCore = {
    id: uuidSchema,
    shiftId: uuidSchema,
    shiftNo: z.string(),
    stationId: uuidSchema,
    businessDate: localDateSchema,
    memberId: uuidSchema,
    staffId: uuidSchema,
    staffCode: z.string(),
    /** KVKK: kişisel veri — yalnızca bu yanıtta döner. */
    staffName: z.string(),
    kind: shiftMemberDutySchema,
    status: shiftClosingStatusSchema,
    expectedAmount: moneySchema,
    expectedWarnings: z.array(shiftClosingWarningSchema),
    cashCounted: moneySchema,
    openingFloat: moneySchema,
    cashDrops: moneySchema,
    cashExpenses: moneySchema,
    cashSales: moneySchema.describe("Satıştan gelen net nakit = sayılan − devir + ara teslim + masraf"),
    nonCashTotal: moneySchema,
    declaredTotal: moneySchema,
    difference: moneySchema.describe("beyan − beklenen (eksi = AÇIK)"),
    toleranceLimit: moneySchema,
    withinTolerance: z.boolean(),
    settlementTarget: shiftSettlementTargetSchema.nullable(),
    settlementCounterpartyId: uuidSchema.nullable(),
    settlementOpenItemId: uuidSchema.nullable(),
    approvalRequestId: uuidSchema.nullable(),
    cashTransactionId: uuidSchema.nullable(),
    handoverFileId: uuidSchema.nullable(),
    explanation: z.string().nullable(),
    rejectedReason: z.string().nullable(),
    submittedAt: timestampSchema.nullable(),
    submittedBy: uuidSchema.nullable(),
    acceptedAt: timestampSchema.nullable(),
    version: z.number().int().min(1),
};
export const shiftClosingSummarySchema = z.object(closingCore);
export const shiftClosingSchema = z.object({
    ...closingCore,
    /**
     * `true` → vardiya henüz `CLOSING` DEĞİL: beklenen tutar "şu ana kadar" TAHMİNİDİR (son okuma kapanış
     * endeksi sayılır) ve hiçbir şey dondurulmamıştır. Hesap teslim edilemez.
     */
    provisional: z.boolean(),
    cashDenominations: z.array(denominationSchema).nullable(),
    tenders: z.array(shiftClosingTenderSchema),
    expectedDetail: shiftExpectedDetailSchema,
    /** Farkın karşılığı; `settleDifference` sonucu (kabulden ÖNCE de önizlenir). */
    settlementPreview: z.object({
        target: shiftSettlementTargetSchema.nullable(),
        amount: moneySchema,
        /** Politika personel dediği hâlde personel carisi olmadığı için gidere düşülecek. */
        fellBackToExpense: z.boolean(),
    }),
});
export const shiftClosingListSchema = z.object({ items: z.array(shiftClosingSummarySchema) });
export const shiftClosingTotalsSchema = z.object({
    expectedAmount: moneySchema,
    declaredTotal: moneySchema,
    shortage: moneySchema.describe("Açıkların toplamı (mutlak)"),
    surplus: moneySchema,
    accepted: z.number().int().nonnegative(),
    pending: z.number().int().nonnegative().describe("Henüz kabul edilmemiş hesap sayısı"),
});
/** Vardiya ayrıntısı + hesap özetleri (B aşamasıyla genişledi). */
export const shiftDetailSchema = shiftSchema.extend({
    closings: z.array(shiftClosingSummarySchema),
    closingTotals: shiftClosingTotalsSchema,
});
export const shiftClosingParamsSchema = z.object({ id: uuidSchema, memberId: uuidSchema });
//# sourceMappingURL=closings.js.map